// /app/bulk-jobs/$bulkJobId — status + inline review view of one bulk job.
//
// v0.2.37+ rewrite (operator feedback: "I cannot see all in one place
// for approve"): instead of just listing children with links to per-
// child detail pages (which forced ~80 clicks to finish 16 posts), this
// page now loads each ready child's variants inline and exposes a
// one-click "Accept & schedule" button per child. Full multi-variant
// review still available via the "View" link to the detail page when
// the operator wants to pick a different variant or edit text.
//
// Loading strategy:
//   - On mount, load the bulk job (which includes child summaries).
//   - For every child with status=ready (variants exist), fetch the
//     generation in parallel via Promise.all. Each fetch is a small
//     payload; the network parallelism is fine for ≤60 children
//     (the global bulk cap).
//   - Auto-poll every 10s while children are still pending/running so
//     the page updates as the worker drains the queue.
//
// Quick-approve flow:
//   1. acceptVersion(versionId) — flips variant.status to 'accepted'
//   2. approve(generationId, {selections:{platform: versionId}}) —
//      creates the post in 'scheduled' status (or 'draft' if no future
//      scheduled_for; the v0.2.36 past-date-bump policy applies)
//   3. Page polls and shows the resulting status.

import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { ScheduleModal } from "../../../components/content/ScheduleModal";
import { useI18n } from "../../../lib/i18n";
import {
  ApiError,
  assetURL,
  bulkGenerations,
  generations,
  type BulkJob,
  type GenerationResponse,
} from "../../../lib/api";

function BulkJobDetail() {
  const { t } = useI18n();
  const { bulkJobId } = Route.useParams();
  const [job, setJob] = useState<BulkJob | null>(null);
  const [err, setErr] = useState<"not_found" | "error" | null>(null);
  // genByChild — child generation_id → full GenerationResponse (with versions).
  // Populated lazily as ready children are detected; cleared on bulkJobId change.
  const [genByChild, setGenByChild] = useState<Record<string, GenerationResponse>>({});
  // Per-row action state. Keyed by child generation_id.
  const [pendingChild, setPendingChild] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    bulkGenerations.get(bulkJobId)
      .then(j => { if (!cancelled) setJob(j); })
      .catch(e => {
        if (cancelled) return;
        setErr(e instanceof ApiError && e.status === 404 ? "not_found" : "error");
      });
    return () => { cancelled = true; };
  }, [bulkJobId]);

  // Auto-poll every 10s while there's outstanding work to do — keeps
  // the page honest as the bulk worker drains the queue from the
  // backend side.
  useEffect(() => {
    if (!job) return;
    const outstanding = job.child_stats.pending + job.child_stats.running;
    if (outstanding === 0) return;
    const h = window.setInterval(() => {
      bulkGenerations.get(bulkJobId).then(setJob).catch(() => { /* ignore */ });
    }, 10_000);
    return () => window.clearInterval(h);
  }, [job, bulkJobId]);

  // Fetch full generations (with versions) for every child that's
  // ready or approved. Re-runs when the children list changes (e.g.,
  // poll moves rows from running→ready). Already-fetched genIds are
  // skipped — no need to refetch when nothing about them changed,
  // unless their status changed (we trigger a refetch then).
  useEffect(() => {
    if (!job?.children) return;
    const toFetch = job.children
      .filter(c => (c.status === "ready" || c.status === "approved") && !genByChild[c.generation_id])
      .map(c => c.generation_id);
    if (toFetch.length === 0) return;
    let cancelled = false;
    Promise.all(
      toFetch.map(id => generations.get(id).catch(() => null)),
    ).then(results => {
      if (cancelled) return;
      setGenByChild(prev => {
        const next = { ...prev };
        for (const r of results) {
          if (r) next[r.generation.id] = r;
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [job?.children, genByChild]);

  async function quickApprove(childId: string, platform: string) {
    const gen = genByChild[childId];
    if (!gen || !gen.versions || gen.versions.length === 0) return;
    // Pick the first pending variant. If somehow all are rejected/
    // accepted already, fall back to the first one (the approve call
    // will still work because the version exists).
    const chosen =
      gen.versions.find(v => v.platform === platform && v.status === "pending") ||
      gen.versions.find(v => v.platform === platform) ||
      gen.versions[0];
    if (!chosen) return;
    setPendingChild(childId);
    setRowErr(prev => ({ ...prev, [childId]: "" }));
    try {
      // Two-step contract: mark variant accepted first (so the approve
      // handler's "selections" map is consistent with version status),
      // then approve which creates the post.
      await generations.acceptVersion(chosen.id);
      await generations.approve(childId, {
        selections: { [platform]: chosen.id },
        ai_disclosure: false,
      });
      // Refresh the bulk job + this child's generation so the row
      // moves to 'approved' immediately.
      const fresh = await bulkGenerations.get(bulkJobId);
      setJob(fresh);
      const freshGen = await generations.get(childId).catch(() => null);
      if (freshGen) {
        setGenByChild(prev => ({ ...prev, [childId]: freshGen }));
      }
    } catch (e) {
      setRowErr(prev => ({
        ...prev,
        [childId]: e instanceof ApiError ? e.message : t("common.errorGeneric"),
      }));
    } finally {
      setPendingChild(null);
    }
  }

  // bulkApproveAll — accepts the first variant per ready child + calls
  // approve for each. Only meaningful when ≥2 children are 'ready'
  // (one child is already a one-button click; multi means it saves
  // real time).
  const readyChildIds = useMemo(
    () => (job?.children ?? [])
      .filter(c => c.status === "ready" && genByChild[c.generation_id])
      .map(c => c.generation_id),
    [job?.children, genByChild],
  );
  const showApproveAll = readyChildIds.length >= 2;
  const [bulkApproving, setBulkApproving] = useState(false);
  async function approveAllReady() {
    if (!showApproveAll || !job) return;
    setBulkApproving(true);
    try {
      // Sequential so errors don't leave a half-state. Slower than
      // parallel, but the approve call is fast (~100ms) so a 16-child
      // bulk completes in 1-2s.
      for (const childId of readyChildIds) {
        const c = job.children?.find(x => x.generation_id === childId);
        if (!c) continue;
        await quickApprove(childId, c.platform);
      }
    } finally {
      setBulkApproving(false);
    }
  }

  // v0.2.49 — optional bulk-approve-with-override. By default the
  // bulk approve preserves each child's spread-date (current behavior
  // via quickApprove). The override path opens ScheduleModal and
  // sends the SAME timestamp for every child — useful when the
  // operator wants to dump all N at once instead of the spread
  // schedule (e.g. "launch campaign Friday 9 AM, all at once").
  const [scheduleAllOpen, setScheduleAllOpen] = useState(false);
  async function approveAllAtOneTime(when: { scheduledFor?: string; saveAsDraft?: boolean }) {
    if (!showApproveAll || !job) return;
    setBulkApproving(true);
    try {
      for (const childId of readyChildIds) {
        const c = job.children?.find(x => x.generation_id === childId);
        if (!c) continue;
        const gen = genByChild[childId];
        const acceptedVersion = gen?.versions.find((v) => v.status === "accepted")
          ?? gen?.versions[0];
        if (!acceptedVersion) continue;
        await generations.acceptVersion(acceptedVersion.id);
        await generations.approve(childId, {
          selections: { [c.platform]: acceptedVersion.id },
          ai_disclosure: false,
          scheduled_for: when.scheduledFor,
          save_as_draft: when.saveAsDraft,
        });
      }
      const fresh = await bulkGenerations.get(bulkJobId);
      setJob(fresh);
      setScheduleAllOpen(false);
    } finally {
      setBulkApproving(false);
    }
  }

  if (err === "not_found") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          <span className="eyebrow">404</span>
          <h2 className="font-display text-xl font-semibold mt-2">{t("notfound.title")}</h2>
          <Link to="/app/content" className="text-secondary hover:text-primary text-sm mt-4 inline-block">
            ← {t("content.title")}
          </Link>
        </Card>
      </div>
    );
  }
  if (err || !job) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          {err ? <p className="text-error">{t("common.errorGeneric")}</p>
            : <p className="text-secondary">{t("common.loading")}</p>}
        </Card>
      </div>
    );
  }

  const s = job.child_stats;
  // Pill tone — green when everything's approved, error when anything
  // failed, info otherwise. Same logic as before; just rendered as
  // part of the new card layout.
  const overallTone =
    s.failed > 0 ? "text-error"
      : s.approved === s.total && s.total > 0 ? "text-success"
      : "text-info";

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2 min-w-0">
          <Link to="/app/content/$brandId" params={{ brandId: job.brand_id }}
                className="text-xs text-muted hover:text-primary ring-nexus rounded inline-block">
            ← {t("content.title")}
          </Link>
          <h1 className="font-display text-3xl font-semibold">{t("content.bulk.detail.title")}</h1>
          <p className="text-xs text-muted font-mono">
            {job.start_date} → {job.end_date} · {job.platforms.join(" · ")}
          </p>
          {job.prompt ? (
            <p className="text-sm text-secondary mt-1">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted">
                {t("content.bulk.detail.topic")}
              </span>{" "}
              {job.prompt}
            </p>
          ) : null}
        </div>
      </div>

      {/* Summary + (optionally) "Approve all" */}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`font-mono uppercase text-xs ${overallTone}`}>{job.status}</span>
            <span className="text-xs text-muted">·</span>
            <span className="text-sm">
              {t("content.bulk.detail.progress")
                .replace("{done}", String(s.approved + s.ready))
                .replace("{total}", String(s.total))}
            </span>
            {s.failed > 0 ? (
              <span className="text-xs text-error font-mono">{s.failed} {t("content.bulk.detail.failed")}</span>
            ) : null}
          </div>
          {showApproveAll ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="primary" loading={bulkApproving}
                      onClick={() => void approveAllReady()}>
                {t("content.bulk.detail.approveAll").replace("{n}", String(readyChildIds.length))}
              </Button>
              {/* v0.2.49 — secondary override: schedule all at one time */}
              <button
                type="button"
                onClick={() => setScheduleAllOpen(true)}
                disabled={bulkApproving}
                className="text-xs text-muted hover:text-secondary underline underline-offset-2 ring-nexus"
                title={t("content.bulk.detail.approveAllOverrideHint")}
              >
                {t("content.bulk.detail.approveAllOverride")}
              </button>
            </div>
          ) : null}
        </div>
        {/* Progress bar */}
        <div className="mt-3 w-full h-2 rounded-full bg-elevated overflow-hidden">
          <div
            className="h-full bg-nexus-red transition-all"
            style={{
              width: `${s.total > 0
                ? Math.round(((s.approved + s.ready) / s.total) * 100)
                : 0}%`,
            }}
            aria-hidden
          />
        </div>
      </Card>

      {/* Children — inline review cards */}
      <Card>
        <h2 className="font-display text-lg font-semibold mb-3">{t("content.bulk.detail.children")}</h2>
        {(!job.children || job.children.length === 0) ? (
          <p className="text-secondary text-sm">{t("content.bulk.detail.noChildren")}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {job.children.map(c => {
              const gen = genByChild[c.generation_id];
              const firstVariant = gen?.versions?.find(v => v.platform === c.platform);
              const isPending = pendingChild === c.generation_id;
              const err = rowErr[c.generation_id];
              return (
                <li key={c.generation_id}
                    className="border border-border-subtle rounded-lg p-3 hover:bg-elevated transition-colors flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-xs uppercase text-secondary">{c.platform}</span>
                      {c.scheduled_for ? (
                        <span className="text-sm text-primary">
                          {new Date(c.scheduled_for).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-sm text-muted">{t("content.bulk.detail.unscheduled")}</span>
                      )}
                    </div>
                    <span className={`text-xs font-mono uppercase ${
                      c.status === "approved" ? "text-success"
                        : c.status === "failed" ? "text-error"
                        : c.status === "ready" ? "text-info"
                        : "text-muted"
                    }`}>
                      {c.status}
                    </span>
                  </div>

                  {/* Inline variant preview — only for ready/approved children */}
                  {firstVariant ? (
                    <div className="flex gap-3 items-start bg-elevated/40 rounded-lg p-3">
                      {firstVariant.image_url ? (
                        <img
                          src={assetURL(firstVariant.image_url)}
                          alt=""
                          className="w-24 h-24 object-cover rounded-md border border-border-subtle flex-shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-md border border-border-subtle flex items-center justify-center text-xs text-muted bg-surface flex-shrink-0">
                          (no image)
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-primary line-clamp-3">
                          {firstVariant.text_body ?? ""}
                        </p>
                        {(firstVariant.hashtags && firstVariant.hashtags.length > 0) ? (
                          <p className="text-xs text-muted mt-1 line-clamp-1">
                            {firstVariant.hashtags.slice(0, 6).map(h => `#${h}`).join(" ")}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {err ? <p className="text-error text-xs" role="alert">{err}</p> : null}

                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    {c.status === "ready" && firstVariant ? (
                      <Button variant="primary"
                              loading={isPending}
                              onClick={() => void quickApprove(c.generation_id, c.platform)}>
                        {t("content.bulk.detail.acceptAndSchedule")}
                      </Button>
                    ) : null}
                    <Link to="/app/generations/$generationId"
                          params={{ generationId: c.generation_id }}
                          className="text-xs text-secondary hover:text-primary underline">
                      {t("content.bulk.detail.viewDetail")}
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* v0.2.49 — bulk approve with single-timestamp override */}
      {scheduleAllOpen ? (
        <ScheduleModal
          count={readyChildIds.length}
          busy={bulkApproving}
          onConfirm={(when) => void approveAllAtOneTime(when)}
          onCancel={() => setScheduleAllOpen(false)}
        />
      ) : null}
    </div>
  );
}

export const Route = createFileRoute("/app/bulk-jobs/$bulkJobId")({
  component: BulkJobDetail,
});
